/**
 * Last Played Games Service
 * Handles local storage for last played games
 */

import { LAST_PLAYED_SECTION_NAME, SECTION_NAMES } from '@repo/common';
import { LAST_PLAYED_STORAGE_KEY } from '../../../constants';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import type IGameSettings from '../../../types/models/IGameSettings';
import { parseGameSettings } from 'shared/utils/gameParsingUtils';
import type { ApiGameSettings } from 'types/api/api.types';

class LastPlayedService {
  private lastPlayedGames: string[] = [];
  private maxGames = 100; // Default max games, can be overridden by topLimit from API
  private playerId: string | null = null;

  /**
   * Set the player ID for user-specific storage
   */
  setPlayerId(playerId: string): void {
    this.playerId = playerId;
  }

  /**
   * Get the storage key for the current player
   */
  private getStorageKey(): string {
    if (this.playerId) {
      return `${LAST_PLAYED_STORAGE_KEY}_${this.playerId}`;
    }
    return LAST_PLAYED_STORAGE_KEY;
  }

  /**
   * Initialize last played games from localStorage
   */
  initializeLastPlayedGames(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (stored) {
        this.lastPlayedGames = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('[LastPlayedService] Failed to load last played games from localStorage:', error);
      this.lastPlayedGames = [];
    }
  }

  /**
   * Add a game key to the last played list
   */
  addLastPlayedGame(gameKey: string): void {
    if (!gameKey) return;

    // Remove the game if it already exists in the list
    this.lastPlayedGames = this.lastPlayedGames.filter(key => key !== gameKey);
    
    // Add the game to the beginning of the list (most recent first)
    this.lastPlayedGames.unshift(gameKey);
    
    // Limit to maxGames
    this.lastPlayedGames = this.lastPlayedGames.slice(0, this.maxGames);
    
    // Save to localStorage
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.lastPlayedGames));
    } catch (error) {
      console.warn('[LastPlayedService] Failed to save last played games to localStorage:', error);
    }
  }

  /**
   * Create LAST_PLAYED section from current games
   */
  createLastPlayedSection(supportedGames: { [gameKey: string]: ApiGameSettings }, existingSections: ISectionSettings[]): ISectionSettings | null {
    if (this.lastPlayedGames.length === 0) {
      return null;
    }

    // Find topLimit from existing LAST_PLAYED section in API response (if any)
    const apiLastPlayedSection = existingSections.find(s => s.name === LAST_PLAYED_SECTION_NAME);
    const topLimit = apiLastPlayedSection?.topLimit || this.maxGames;
    
    // Update maxGames to respect API configuration for future additions
    if (apiLastPlayedSection?.topLimit) {
      this.maxGames = apiLastPlayedSection.topLimit;
    }

    // Build a Set of TOP game keys from existing sections
    const topSection = existingSections.find(s => s.name === SECTION_NAMES.TOP);
    const topGamesSet = new Set<string>(
      topSection?.games?.map(g => g.gameKey) || []
    );

    // Map game keys to game settings
    const games: IGameSettings[] = [];
    const limitedGameKeys = this.lastPlayedGames.slice(0, topLimit);

    limitedGameKeys.forEach((gameKey, index) => {
      const gameData = supportedGames[gameKey];

      if (gameData) {
        // Use parseGameSettings utility to properly handle bet ranges
        const mutatedGame = {
          gameKey: gameData.gameKey,
          imageLobby: 'vertical' as const,
          imageSection: 'vertical' as const,
          order: index, // Order by recency
        };

        const parsedGame = parseGameSettings({
          mutatedGame,
          gameData,
          forTournament: false,
          isTopGame: topGamesSet.has(gameKey)
        });
        if(parsedGame) {
          games.push(parsedGame);
        }
      }
    });

    if (games.length === 0) {
      return null;
    }

    return {
      name: LAST_PLAYED_SECTION_NAME,
      jackpotName: null,
      icon: 'last_played_icon',
      games,
      gridBlocks: 0,
      banner: false,
      topLimit,
    };
  }

  /**
   * Get current last played games
   */
  getLastPlayedGames(): string[] {
    return [...this.lastPlayedGames];
  }

  /**
   * Set max games limit
   */
  setMaxGames(max: number): void {
    this.maxGames = max;
  }
}

export const lastPlayedService = new LastPlayedService();
